import { useEffect, useState } from "react";
import { fetchGamification } from "../../context/actions/gamification";

const MEDALS = { 1: "🥇", 2: "🥈", 3: "🥉" };

function Row({ entry, index }) {
  if (entry.gap) {
    return (
      <tr>
        <td colSpan={4} className="text-center py-2">
          <div className="flex items-center justify-center gap-1">
            {[0, 1, 2].map((i) => (
              <div key={i} className="w-1.5 h-1.5 rounded-full bg-gray-300" />
            ))}
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr
      className={`border-b border-gray-50 transition-colors ${
        entry.is_me ? "bg-[#FEF0EB]" : "hover:bg-gray-50"
      }`}
    >
      <td className="py-3 pl-4 pr-2 w-12 text-center">
        {MEDALS[entry.rank] ? (
          <span className="text-xl">{MEDALS[entry.rank]}</span>
        ) : (
          <span className="text-sm font-bold text-gray-400">#{entry.rank}</span>
        )}
      </td>
      <td className="py-3 pr-3">
        <div className="flex items-center gap-3">
          {entry.avatar ? (
            <img
              src={entry.avatar}
              alt={entry.name}
              className="w-9 h-9 rounded-full object-cover"
            />
          ) : (
            <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-sm font-bold text-gray-500">
              {entry.name?.charAt(0) ?? "?"}
            </div>
          )}
          <span
            className={`text-sm font-medium ${
              entry.is_me ? "text-[#d15424] font-semibold" : "text-gray-800"
            }`}
          >
            {entry.is_me ? "You" : entry.name}
          </span>
          {entry.is_me && (
            <span className="text-xs bg-[#d15424] text-white px-2 py-0.5 rounded-full">
              You
            </span>
          )}
        </div>
      </td>
      <td className="py-3 text-right pr-6">
        <span className="text-sm font-bold text-[#294a93]">{entry.points}</span>
        <span className="text-xs text-gray-400 ml-1">pts</span>
      </td>
    </tr>
  );
}

export default function LeaderboardPage() {
  const [gamification, setGamification] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGamification().then((res) => {
      setGamification(res);
      setLoading(false);
    });
  }, []);

  const entries = gamification?.leaderboard?.entries ?? [];
  const streak  = gamification?.streak?.current ?? 0;
  const points  = gamification?.points ?? 0;

  return (
    <div style={{ background: "#f7f7f7", minHeight: "100vh", padding: "36px 0" }}>
      <div className="innersection" style={{ maxWidth: 680 }}>
        {/* Page header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#001931] flex items-center gap-2">
            🏆 Leaderboard
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Top performers in your cohort — keep earning points to climb the ranks!
          </p>
        </div>

        {/* Your stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-gradient-to-br from-[#d15424] to-[#ff8c5a] rounded-2xl p-4 text-white">
            <p className="text-xs opacity-80">🔥 Current Streak</p>
            <p className="text-3xl font-bold mt-1">{loading ? "—" : streak}<span className="text-sm font-normal ml-1 opacity-90">days</span></p>
          </div>
          <div className="bg-gradient-to-br from-[#294a93] to-[#3b60c4] rounded-2xl p-4 text-white">
            <p className="text-xs opacity-80">⭐ Your Points</p>
            <p className="text-3xl font-bold mt-1">{loading ? "—" : points}</p>
          </div>
        </div>

        {/* Leaderboard table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <h2 className="font-semibold text-gray-800 text-sm">Cohort Rankings</h2>
          </div>

          {loading ? (
            <div className="p-6 space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 bg-gray-100 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : entries.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-4xl mb-3">📊</p>
              <p className="text-sm font-medium text-gray-600">No leaderboard data yet</p>
              <p className="text-xs text-gray-400 mt-1">
                Start earning points by attending classes, submitting projects, and answering daily questions!
              </p>
            </div>
          ) : (
            <table className="w-full">
              <tbody>
                {entries.map((entry, i) => (
                  <Row key={entry.gap ? `gap-${i}` : `${entry.rank}-${entry.name}`} entry={entry} index={i} />
                ))}
              </tbody>
            </table>
          )}

          {/* How to earn points */}
          <div className="px-4 py-4 bg-gray-50 border-t border-gray-100">
            <p className="text-xs font-semibold text-gray-600 mb-3">How to earn points</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              {[
                { icon: "📅", label: "Attend a class",           pts: "+10",  positive: true  },
                { icon: "📁", label: "Submit a project",          pts: "+20",  positive: true  },
                { icon: "✅", label: "Pass an assessment",         pts: "+50",  positive: true  },
                { icon: "🏆", label: "Project grade A",           pts: "+50",  positive: true  },
                { icon: "🥈", label: "Project grade B",           pts: "+40",  positive: true  },
                { icon: "🥉", label: "Project grade C",           pts: "+30",  positive: true  },
                { icon: "🧠", label: "Daily question correct",    pts: "+5",   positive: true  },
                { icon: "🔁", label: "Assessment retry",          pts: "−5",   positive: false },
              ].map(({ icon, label, pts, positive }) => (
                <div key={label} className="flex items-center gap-2 text-xs text-gray-500">
                  <span>{icon}</span>
                  <span className="flex-1 leading-tight">{label}</span>
                  <span className={`font-bold flex-shrink-0 ${positive ? "text-[#294a93]" : "text-rose-500"}`}>{pts}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
